import { Doc } from "@/components/Doc/Doc";
import { FlashOnChangeTD } from "@/components/FlashOnChange/FlashOnChange";
import { LabelExplainer } from "@/components/LabelExplainer/LabelExplainer";
import { Panel } from "@/components/Panel/Panel";
import {
  acquisitionInvestorsPercentCommonEquityAtom,
  acquisitionInvestorsPercentPreferredEquityAtom,
  coInvestorsPercentPreferredEquityAtom,
  commonEquityTotalPercentAtom,
  preferredEquityTotalPercentAtom,
  promoteSearchersCarryPercentCommonEquityAtom,
  searcherCoInvestPercentCommonEquityAtom,
  searchPhaseInvestorsPercentCommonEquityAtom,
  searchPhaseInvestorsPercentPreferredEquityAtom,
  sellerRolloverPercentCommonEquityAtom,
  sellerRolloverPercentPreferredEquityAtom,
} from "@/financeModels/owasco/companyOwnership";
import { isExternalEquityAtom } from "@/financeModels/owasco/general";
import {
  allInvestorTypesAtom,
  InvestorType,
} from "@/financeModels/owasco/returnMetrics";
import { monospaceFontFamily } from "@/theme";
import { formatDecimalPercentTwoPlaces } from "@/utils/format";

import { Table } from "@mantine/core";
import { useAtomValue } from "jotai";

export const CapTableOwnership = () => {
  const investorValues = useAtomValue(allInvestorTypesAtom);
  const isExternalEquity = useAtomValue(isExternalEquityAtom);

  function isVisible(id: InvestorType["id"]) {
    return (
      investorValues
        .filter((row) => row.isVisible)
        .filter((row) => row.id === id).length > 0
    );
  }

  let coInvestCommonEquity = useAtomValue(
    searcherCoInvestPercentCommonEquityAtom,
  );
  if (!isExternalEquity) {
    coInvestCommonEquity = 1;
  }

  const values = [
    {
      preferred: useAtomValue(searchPhaseInvestorsPercentPreferredEquityAtom),
      common: useAtomValue(searchPhaseInvestorsPercentCommonEquityAtom),
      isVisible: isVisible("searchPhase"),
    },
    {
      preferred: useAtomValue(acquisitionInvestorsPercentPreferredEquityAtom),
      common: useAtomValue(acquisitionInvestorsPercentCommonEquityAtom),
      isVisible: isVisible("acquisition"),
    },
    {
      preferred: useAtomValue(coInvestorsPercentPreferredEquityAtom),
      common: coInvestCommonEquity,
      isVisible: true,
    },
    {
      preferred: useAtomValue(sellerRolloverPercentPreferredEquityAtom),
      common: useAtomValue(sellerRolloverPercentCommonEquityAtom),
      isVisible: isVisible("rollover"),
    },
    {
      preferred: null,
      common: useAtomValue(promoteSearchersCarryPercentCommonEquityAtom),
      isVisible: isVisible("promote"),
    },
    {
      preferred: useAtomValue(preferredEquityTotalPercentAtom),
      common: useAtomValue(commonEquityTotalPercentAtom),
      isVisible: true,
      highlight: true,
    },
  ];

  return (
    <Panel p={0} w={200}>
      <Table striped withColumnBorders withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>
              <LabelExplainer
                label={
                  <span style={{ whiteSpace: "nowrap", lineHeight: 1.6 }}>
                    Preferred <br />
                    Equity
                  </span>
                }
                explanation={
                  <Doc path="@/docs/pages/model/roi/cap-table-prefered-equity-percent.mdx" />
                }
              />
            </Table.Th>

            <Table.Th>
              <LabelExplainer
                lh={1.6}
                label={"Common Equity"}
                explanation={
                  <Doc path="@/docs/pages/model/roi/cap-table-common-equity-percent.mdx" />
                }
              />
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody style={{ whiteSpace: "nowrap" }}>
          {values
            .filter((row) => row.isVisible)
            .map((row, i) => (
              <Table.Tr
                key={i}
                ff={monospaceFontFamily}
                ta={"right"}
                {...(i === values.length - 1
                  ? { fw: 700, bg: "infomatic.0" }
                  : {})}
                {...(row?.highlight ? { fw: 700, bg: "infomatic.0" } : {})}
              >
                <FlashOnChangeTD content={row.preferred?.toString()}>
                  {row.preferred !== null
                    ? formatDecimalPercentTwoPlaces(Number(row.preferred))
                    : ""}
                </FlashOnChangeTD>

                <FlashOnChangeTD content={row.common?.toString()}>
                  {row.common !== null
                    ? formatDecimalPercentTwoPlaces(Number(row.common))
                    : ""}
                </FlashOnChangeTD>
              </Table.Tr>
            ))}
        </Table.Tbody>
      </Table>
    </Panel>
  );
};
